import { CANVAS, EDGES, NODES, NODE_SIZE } from '../utils/flow';
import { kindLabel } from '../utils/notice';

import '../styles/flow.css';

// The flow of a task, drawn.
//
// NOTHING HERE HOLDS STATE, and that is what keeps it honest. The lit edge is
// not a timer somebody starts: it is keyed by the id of the newest notice of
// that kind that came in through the channel, so React remounts that one path
// and its CSS animation runs once. No notice, no key change, no animation —
// the screen cannot pulse without an event having arrived.
//
// `pathLength="100"` is why one dash pattern fits every edge: it tells the
// browser to treat each path as 100 units long whatever its real length, so a
// dash of 12 is the same visual slug on the short hop and on the long lane.

export default function Flow({ rows, counts }) {
    // The last notice per kind, and the traffic per kind, in one pass over the
    // feed. The rows are newest first, so the first live hit wins.
    const lit = {};
    const traffic = {};
    for (const row of rows) {
        traffic[row.kind] = (traffic[row.kind] ?? 0) + 1;
        if (row.live && lit[row.kind] === undefined) lit[row.kind] = row.id;
    }

    return (
        <div className="flow">
            <svg
                className="flow__canvas"
                viewBox={`0 0 ${CANVAS.width} ${CANVAS.height}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="El recorrido de una tarea, con cuántas hay en cada estado"
            >
                <defs>
                    <marker
                        id="flow-arrow" markerWidth="7" markerHeight="7"
                        refX="6" refY="3.5" orient="auto"
                    >
                        <path d="M 0 0 L 7 3.5 L 0 7 z" className="flow__arrowhead" />
                    </marker>
                </defs>

                {EDGES.map(edge => (
                    <Edge
                        key={edge.kind}
                        edge={edge}
                        traffic={traffic[edge.kind] ?? 0}
                        litBy={lit[edge.kind]}
                    />
                ))}

                {NODES.map(node => (
                    <Node key={node.state} node={node} count={counts[node.state]} />
                ))}
            </svg>
        </div>
    );
}

function Edge({ edge, traffic, litBy }) {
    const shape = [
        'flow__edge',
        edge.back ? 'flow__edge--back' : '',
        edge.exit ? 'flow__edge--exit' : '',
        edge.beat ? 'flow__edge--beat' : '',
        traffic > 0 ? 'flow__edge--used' : '',
    ].filter(Boolean).join(' ');

    return (
        <g className={shape}>
            <path className="flow__line" d={edge.path} pathLength="100" markerEnd="url(#flow-arrow)" />

            {/* Remounted on every arrival, which is what restarts the run. */}
            {litBy !== undefined && (
                <path key={litBy} className="flow__run" d={edge.path} pathLength="100" />
            )}

            {/* Placed, never flowed along the path: a label that follows its
                line comes out mirrored on the way back and sideways on the way
                down. Horizontal always, whatever the edge does. */}
            <text
                className="flow__label"
                x={edge.label.x} y={edge.label.y}
                textAnchor={edge.label.anchor}
            >
                {kindLabel(edge.kind).toLowerCase()}
                {traffic > 0 && <tspan className="flow__traffic"> · {traffic}</tspan>}
            </text>
        </g>
    );
}

// THREE LINES, EACH WITH ITS OWN ROW. The count used to share a baseline with
// the hint and right-align against it, so "Descartada · 0 · Desde cualquier
// estado" collided into itself. Nothing here overlaps anything, at any count
// and at any hint length.
//
// `claimed` LATE MIENTRAS HAYA ALGUIEN TRABAJANDO. Es el único estado donde hay
// un agente con las manos en el código ahora mismo, y el dibujo lo decía igual
// que «Lista», que es una cola quieta. La animación no es decorativa: está
// atada al número, así que solo late si hay al menos una tarea tomada, y se
// apaga sola cuando el número baja a cero.
function Node({ node, count }) {
    const beating = node.state === 'claimed' && count > 0;

    return (
        <g className={`flow__node flow__node--${node.state}${beating ? ' flow__node--beating' : ''}`}>
            <rect
                x={node.x} y={node.y}
                width={NODE_SIZE.width} height={NODE_SIZE.height}
                rx="8"
                className="flow__box"
            />
            <text x={node.x + 18} y={node.y + 30} className="flow__name">{node.label}</text>

            {/* Tres puntos trabajando, al estilo de «está escribiendo». Van
                arriba a la derecha, donde no chocan con ninguna de las tres
                líneas de texto. */}
            {beating && (
                <g className="flow__working" aria-hidden="true">
                    {[0, 1, 2].map(i => (
                        <circle
                            key={i}
                            className="flow__working-dot"
                            cx={node.x + NODE_SIZE.width - 46 + i * 14}
                            cy={node.y + 26}
                            r="4"
                            style={{ animationDelay: `${i * 0.18}s` }}
                        />
                    ))}
                </g>
            )}
            <text x={node.x + 18} y={node.y + 68} className="flow__count">
                {/* Null is "not counted yet", which is not zero. A zero drawn
                    while the request is in flight is a wrong number, and this
                    screen exists to not show those. */}
                {count == null ? '—' : count}
            </text>
            <text x={node.x + 18} y={node.y + 88} className="flow__hint">{node.hint}</text>
        </g>
    );
}
